// components/NotificationPermissionPrompt.jsx
// Itemsatış-style browser notification activation prompt for customers.
//
// - Appears only after login, only when the browser supports push and the
//   permission is still "default" (never nags on granted/denied).
// - Respects a dismiss cooldown so "Sonra" isn't shown again for a while.
// - On granted it silently ensures the subscription is saved.

import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { FaBell, FaTimes } from 'react-icons/fa'
import {
    isPushSupported, getPushPermission, requestPushPermission,
    subscribeToPush, ensurePushSubscription
} from '../utils/pushNotifications'

const DISMISS_KEY = 'itemzo_push_prompt_dismissed_at'
const DENIED_HINT_KEY = 'itemzo_push_denied_hint_at'
const COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000 // 3 gün

const recently = (key) => {
    try {
        const ts = Number(localStorage.getItem(key) || 0)
        return ts && Date.now() - ts < COOLDOWN_MS
    } catch (e) { return false }
}
const stamp = (key) => { try { localStorage.setItem(key, String(Date.now())) } catch (e) { /* ignore */ } }

const NotificationPermissionPrompt = () => {
    const { t } = useTranslation()
    const { userInfo, authChecked } = useSelector(state => state.auth)
    const [open, setOpen] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        if (!authChecked || !userInfo) return
        if (!isPushSupported()) return

        const permission = getPushPermission()

        if (permission === 'granted') {
            // Already allowed — make sure this device is registered, no UI.
            ensurePushSubscription()
            return
        }

        if (permission === 'denied') {
            // Don't re-ask; show a gentle one-time settings hint.
            if (!recently(DENIED_HINT_KEY)) {
                stamp(DENIED_HINT_KEY)
                toast(t('notifications.browser.blockedHint'), { icon: '🔔' })
            }
            return
        }

        // permission === 'default' → offer activation after a short delay.
        if (recently(DISMISS_KEY)) return
        const timer = setTimeout(() => setOpen(true), 1500)
        return () => clearTimeout(timer)
    }, [authChecked, userInfo])

    const handleActivate = async () => {
        setBusy(true)
        try {
            const permission = await requestPushPermission()
            if (permission === 'granted') {
                const ok = await subscribeToPush()
                if (ok) toast.success(t('notifications.browser.enabled'))
                else toast.error(t('notifications.browser.failedRetry'))
            } else if (permission === 'denied') {
                stamp(DENIED_HINT_KEY)
                toast(t('notifications.browser.blockedHint'), { icon: '🔔' })
            }
        } finally {
            setBusy(false)
            stamp(DISMISS_KEY)
            setOpen(false)
        }
    }

    const handleLater = () => {
        stamp(DISMISS_KEY)
        setOpen(false)
    }

    if (!open) return null

    return (
        <div className='fixed inset-0 z-[10000] flex items-end justify-center bg-slate-950/50 p-4 backdrop-blur-sm sm:items-center'>
            <div
                role='dialog'
                aria-modal='true'
                aria-labelledby='push-prompt-title'
                className='relative w-full max-w-sm overflow-hidden rounded-2xl border border-brand-100 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.28)]'
            >
                <button
                    type='button'
                    onClick={handleLater}
                    aria-label={t('notifications.browser.close')}
                    className='absolute right-3 top-3 text-slate-400 transition hover:text-slate-600'
                >
                    <FaTimes size={16} />
                </button>

                <div className='flex flex-col items-center px-6 pb-6 pt-8 text-center'>
                    <span className='mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg'>
                        <FaBell size={26} />
                    </span>
                    <h2 id='push-prompt-title' className='text-lg font-bold text-slate-900'>
                        {t('notifications.browser.title')}
                    </h2>
                    <p className='mt-2 text-sm leading-relaxed text-slate-500'>
                        {t('notifications.browser.subtitle')}
                    </p>

                    <div className='mt-6 flex w-full gap-3'>
                        <button
                            type='button'
                            onClick={handleLater}
                            disabled={busy}
                            className='flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60'
                        >
                            {t('notifications.browser.later')}
                        </button>
                        <button
                            type='button'
                            onClick={handleActivate}
                            disabled={busy}
                            className='flex-1 rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-brand-700 disabled:opacity-60'
                        >
                            {busy ? t('notifications.browser.wait') : t('notifications.browser.activate')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default NotificationPermissionPrompt
